#!/usr/bin/env node

import { program } from 'commander';
import chalk from 'chalk';
import { CodeboxRegistry } from './index.js';

const registry = new CodeboxRegistry();

program
  .name('codebox-registry')
  .description('Service registry and monitoring for deployed applications')
  .version('0.1.0');

// 启动命令
program
  .command('start')
  .description('Start the service registry server')
  .option('-p, --port <number>', 'API server port', '9091')
  .option('-h, --host <string>', 'API server host', 'localhost')
  .option('--no-api', 'Disable API server')
  .option('--no-health-check', 'Disable health checks')
  .action(async (options) => {
    try {
      const config = {
        api: {
          enabled: options.api,
          port: parseInt(options.port),
          host: options.host
        },
        healthCheck: {
          enabled: options.healthCheck,
          defaultInterval: 60000,
          defaultTimeout: 5000,
          defaultRetries: 3
        }
      };

      const registry = new CodeboxRegistry(config);
      await registry.start();

      console.log(chalk.green('✅ Service registry started'));
      
      if (options.api) {
        console.log(chalk.blue(`API server: http://${options.host}:${options.port}`));
      }

      // 优雅关闭
      process.on('SIGINT', async () => {
        console.log(chalk.yellow('\n⏹️  Shutting down...'));
        await registry.stop();
        process.exit(0);
      });

      process.on('SIGTERM', async () => {
        await registry.stop();
        process.exit(0);
      });
    } catch (error: any) {
      console.error(chalk.red('❌ Failed to start:'), error.message);
      process.exit(1);
    }
  });

// 列出服务
program
  .command('list')
  .description('List all registered services')
  .option('-s, --status <status>', 'Filter by status')
  .option('-t, --type <type>', 'Filter by type')
  .action((options) => {
    const services = registry.listServices(options);
    
    if (services.length === 0) {
      console.log(chalk.yellow('No services found'));
      return;
    }

    console.log(chalk.bold('\nRegistered Services:\n'));
    
    services.forEach(service => {
      const statusColors = {
        running: chalk.green,
        stopped: chalk.gray,
        unhealthy: chalk.red,
        unknown: chalk.yellow,
        starting: chalk.cyan,
        stopping: chalk.yellow,
        error: chalk.red
      };
      const statusColor = statusColors[service.status as keyof typeof statusColors] || chalk.white;

      console.log(`${chalk.bold(service.name)} (${service.id})`);
      console.log(`  Status: ${statusColor(service.status)}`);
      console.log(`  Type: ${service.type}`);
      console.log(`  URL: ${service.url}`);
      console.log(`  Version: ${service.version}`);
      console.log(`  Deployed: ${new Date(service.deployedAt).toLocaleString()}`);
      
      if (service.lastHealthCheck) {
        console.log(`  Last Check: ${new Date(service.lastHealthCheck).toLocaleString()}`);
      }
      
      console.log();
    });
  });

// 显示统计信息
program
  .command('stats')
  .description('Show registry statistics')
  .action(() => {
    const stats = registry.getStatistics();
    
    console.log(chalk.bold('\nService Statistics:\n'));
    console.log(`Total Services: ${stats.services.total}`);
    console.log(`Running: ${chalk.green(stats.services.running)}`);
    console.log(`Stopped: ${chalk.gray(stats.services.stopped)}`);
    console.log(`Unhealthy: ${chalk.red(stats.services.unhealthy)}`);
    console.log(`Unknown: ${chalk.yellow(stats.services.unknown)}`);
    
    console.log(chalk.bold('\nHealth Checker:\n'));
    console.log(`Active Checks: ${stats.healthChecker.active}`);
    console.log(`Services Monitored: ${stats.healthChecker.services.length}`);
  });

// 健康检查命令
program
  .command('check <serviceId>')
  .description('Perform health check on a service')
  .action(async (serviceId) => {
    try {
      const service = registry.getService(serviceId);
      if (!service) {
        console.error(chalk.red('Service not found'));
        process.exit(1);
      }

      console.log(chalk.blue(`Checking health of ${service.name}...`));
      
      const result = await registry.getHealthChecker().performHealthCheck(service);
      
      if (result.success) {
        console.log(chalk.green('✅ Health check passed'));
        if (result.responseTime) {
          console.log(`Response time: ${result.responseTime}ms`);
        }
      } else {
        console.log(chalk.red('❌ Health check failed'));
        if (result.error) {
          console.log(`Error: ${result.error}`);
        }
      }
    } catch (error: any) {
      console.error(chalk.red('Failed:'), error.message);
      process.exit(1);
    }
  });

// 清理命令
program
  .command('cleanup')
  .description('Clean up old data')
  .option('-d, --days <number>', 'Retention days', '30')
  .action((options) => {
    const days = parseInt(options.days);
    console.log(chalk.blue(`Cleaning up data older than ${days} days...`));
    
    registry.getDatabase().cleanupOldData(days);
    
    console.log(chalk.green('✅ Cleanup completed'));
  });

program.parse();